import React, { useRef, useState, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import {
  Pencil,
  Circle,
  Square,
  ArrowUpRight,
  Type,
  Eraser,
  RotateCcw,
  MousePointer,
  Maximize2,
} from 'lucide-react';

type Tool = 'select' | 'pen' | 'rectangle' | 'circle' | 'arrow' | 'text' | 'eraser';

interface Annotation {
  tool: Tool;
  points: { x: number; y: number }[];
  color: string;
  lineWidth: number;
  text?: string;
}

interface ScreenshotAnnotatorProps {
  screenshotSrc: string;
  onAnnotatedScreenshot: (annotatedDataUrl: string) => void;
  onOpenFullPreview?: () => void;
  /** When true, uses larger canvas sizing for fullscreen lightbox */
  fullscreen?: boolean;
}

const TOOLS: { id: Tool; icon: React.ElementType; label: string }[] = [
  { id: 'select', icon: MousePointer, label: 'Sélection' },
  { id: 'pen', icon: Pencil, label: 'Crayon' },
  { id: 'rectangle', icon: Square, label: 'Rectangle' },
  { id: 'circle', icon: Circle, label: 'Cercle' },
  { id: 'arrow', icon: ArrowUpRight, label: 'Flèche' },
  { id: 'text', icon: Type, label: 'Texte' },
  { id: 'eraser', icon: Eraser, label: 'Gomme' },
];

const COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#a855f7', '#ffffff'];
const LINE_WIDTHS = [2, 4, 6];

export function ScreenshotAnnotator({
  screenshotSrc,
  onAnnotatedScreenshot,
  onOpenFullPreview,
  fullscreen = false,
}: Readonly<ScreenshotAnnotatorProps>) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const [activeTool, setActiveTool] = useState<Tool>('select');
  const [activeColor, setActiveColor] = useState('#ef4444');
  const [activeLineWidth, setActiveLineWidth] = useState(4);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [currentAnnotation, setCurrentAnnotation] = useState<Annotation | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [textInput, setTextInput] = useState<{ x: number; y: number } | null>(null);
  const [textValue, setTextValue] = useState('');
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);

  // Load image and set canvas size
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imgRef.current = img;
      setImageLoaded(true);
      updateCanvasSize(img);
    };
    img.src = screenshotSrc;
  }, [screenshotSrc]);

  const updateCanvasSize = useCallback((img: HTMLImageElement) => {
    const container = containerRef.current;
    if (!container) return;
    const maxW = container.clientWidth;
    const maxH = fullscreen ? window.innerHeight * 0.8 : 300;
    const ratio = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight, 1);
    setCanvasSize({
      width: Math.round(img.naturalWidth * ratio),
      height: Math.round(img.naturalHeight * ratio),
    });
  }, [fullscreen]);

  // Redraw canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !imageLoaded) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const allAnnotations = currentAnnotation
      ? [...annotations, currentAnnotation]
      : annotations;

    for (const ann of allAnnotations) {
      drawAnnotation(ctx, ann);
    }
  }, [annotations, currentAnnotation, canvasSize, imageLoaded]);

  // Export annotated image whenever annotations change
  useEffect(() => {
    if (!canvasRef.current || !imageLoaded) return;
    const timeout = setTimeout(() => {
      const dataUrl = canvasRef.current?.toDataURL('image/png', 0.9);
      if (dataUrl) onAnnotatedScreenshot(dataUrl);
    }, 100);
    return () => clearTimeout(timeout);
  }, [annotations, imageLoaded, onAnnotatedScreenshot]);

  function drawAnnotation(ctx: CanvasRenderingContext2D, ann: Annotation) {
    ctx.strokeStyle = ann.color;
    ctx.fillStyle = ann.color;
    ctx.lineWidth = ann.lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    switch (ann.tool) {
      case 'pen': {
        if (ann.points.length < 2) return;
        ctx.beginPath();
        ctx.moveTo(ann.points[0].x, ann.points[0].y);
        for (let i = 1; i < ann.points.length; i++) {
          ctx.lineTo(ann.points[i].x, ann.points[i].y);
        }
        ctx.stroke();
        break;
      }
      case 'eraser':
        // Eraser doesn't draw — it removes annotations on mouseUp
        break;
      case 'rectangle': {
        if (ann.points.length < 2) return;
        const [p1, p2] = [ann.points[0], ann.points[ann.points.length - 1]];
        ctx.strokeRect(p1.x, p1.y, p2.x - p1.x, p2.y - p1.y);
        break;
      }
      case 'circle': {
        if (ann.points.length < 2) return;
        const [c1, c2] = [ann.points[0], ann.points[ann.points.length - 1]];
        const rx = Math.abs(c2.x - c1.x) / 2;
        const ry = Math.abs(c2.y - c1.y) / 2;
        const cx = (c1.x + c2.x) / 2;
        const cy = (c1.y + c2.y) / 2;
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        ctx.stroke();
        break;
      }
      case 'arrow': {
        if (ann.points.length < 2) return;
        const [a1, a2] = [ann.points[0], ann.points[ann.points.length - 1]];
        const angle = Math.atan2(a2.y - a1.y, a2.x - a1.x);
        const headLen = 14;
        ctx.beginPath();
        ctx.moveTo(a1.x, a1.y);
        ctx.lineTo(a2.x, a2.y);
        ctx.stroke();
        // Arrowhead
        ctx.beginPath();
        ctx.moveTo(a2.x, a2.y);
        ctx.lineTo(
          a2.x - headLen * Math.cos(angle - Math.PI / 6),
          a2.y - headLen * Math.sin(angle - Math.PI / 6)
        );
        ctx.lineTo(
          a2.x - headLen * Math.cos(angle + Math.PI / 6),
          a2.y - headLen * Math.sin(angle + Math.PI / 6)
        );
        ctx.closePath();
        ctx.fill();
        break;
      }
      case 'text': {
        if (!ann.text || ann.points.length === 0) return;
        ctx.font = `bold ${Math.max(14, ann.lineWidth * 4)}px sans-serif`;
        ctx.fillStyle = ann.color;
        // Text background
        const metrics = ctx.measureText(ann.text);
        const textH = ann.lineWidth * 4 + 4;
        ctx.save();
        ctx.fillStyle = 'rgba(0,0,0,0.6)';
        ctx.fillRect(ann.points[0].x - 2, ann.points[0].y - textH, metrics.width + 4, textH + 4);
        ctx.restore();
        ctx.fillStyle = ann.color;
        ctx.fillText(ann.text, ann.points[0].x, ann.points[0].y);
        break;
      }
    }
  }

  function getCanvasCoords(e: React.MouseEvent): { x: number; y: number } {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvas.width,
      y: ((e.clientY - rect.top) / rect.height) * canvas.height,
    };
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (activeTool === 'select') return;
    const coords = getCanvasCoords(e);

    if (activeTool === 'text') {
      setTextInput(coords);
      setTextValue('');
      return;
    }

    setIsDrawing(true);
    setCurrentAnnotation({
      tool: activeTool,
      points: [coords],
      color: activeColor,
      lineWidth: activeLineWidth,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDrawing || !currentAnnotation) return;
    const coords = getCanvasCoords(e);

    if (activeTool === 'pen' || activeTool === 'eraser') {
      setCurrentAnnotation((prev) =>
        prev ? { ...prev, points: [...prev.points, coords] } : null
      );
    } else {
      setCurrentAnnotation((prev) =>
        prev ? { ...prev, points: [prev.points[0], coords] } : null
      );
    }
  };

  const handleMouseUp = () => {
    if (!isDrawing || !currentAnnotation) return;
    setIsDrawing(false);

    if (currentAnnotation.tool === 'eraser') {
      // Remove any annotation whose bounding box intersects the eraser path
      const eraserPoints = currentAnnotation.points;
      setAnnotations((prev) =>
        prev.filter((ann) => !annotationIntersectsPath(ann, eraserPoints, currentAnnotation.lineWidth * 4))
      );
    } else if (currentAnnotation.points.length >= 1) {
      setAnnotations((prev) => [...prev, currentAnnotation]);
    }
    setCurrentAnnotation(null);
  };

  function annotationIntersectsPath(
    ann: Annotation,
    eraserPoints: { x: number; y: number }[],
    radius: number
  ): boolean {
    // Get all relevant points of the annotation
    const annPoints = ann.points;
    // Check bounding box of annotation
    const xs = annPoints.map((p) => p.x);
    const ys = annPoints.map((p) => p.y);
    const minX = Math.min(...xs) - radius;
    const maxX = Math.max(...xs) + radius;
    const minY = Math.min(...ys) - radius;
    const maxY = Math.max(...ys) + radius;

    return eraserPoints.some((ep) => ep.x >= minX && ep.x <= maxX && ep.y >= minY && ep.y <= maxY);
  }

  const handleTextSubmit = () => {
    if (!textInput || !textValue.trim()) {
      setTextInput(null);
      return;
    }
    setAnnotations((prev) => [
      ...prev,
      {
        tool: 'text' as Tool,
        points: [textInput],
        color: activeColor,
        lineWidth: activeLineWidth,
        text: textValue.trim(),
      },
    ]);
    setTextInput(null);
    setTextValue('');
  };

  const undo = () => setAnnotations((prev) => prev.slice(0, -1));

  const getCursorStyle = (tool: Tool): string => {
    switch (tool) {
      case 'select':
        return 'default';
      case 'text':
        return 'text';
      case 'eraser':
        return 'cell';
      default:
        return 'crosshair';
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-1">
        {/* Left Toolbar */}
        <div className="flex flex-col gap-1 p-1.5 rounded-lg border border-border bg-muted/40 shrink-0">
          {TOOLS.map((t) => (
            <button
              key={t.id}
              onClick={() => { setActiveTool(t.id); setTextInput(null); }}
              className={cn(
                'p-2 rounded-md transition-colors',
                activeTool === t.id
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )}
              title={t.label}
            >
              <t.icon className="h-4 w-4" />
            </button>
          ))}

          <div className="border-t border-border my-1" />

          {/* Colors */}
          <div className="flex flex-col gap-1 items-center">
            {COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setActiveColor(c)}
                className={cn(
                  'w-5 h-5 rounded-full border-2 transition-transform',
                  activeColor === c ? 'border-foreground scale-110' : 'border-transparent'
                )}
                style={{ backgroundColor: c }}
                title={c}
              />
            ))}
          </div>

          <div className="border-t border-border my-1" />

          {/* Line width */}
          <div className="flex flex-col gap-1 items-center">
            {LINE_WIDTHS.map((w) => (
              <button
                key={w}
                onClick={() => setActiveLineWidth(w)}
                className={cn(
                  'w-6 h-6 rounded flex items-center justify-center transition-colors',
                  activeLineWidth === w
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent'
                )}
                title={`Épaisseur ${w}`}
              >
                <div
                  className="rounded-full bg-current"
                  style={{ width: w + 2, height: w + 2 }}
                />
              </button>
            ))}
          </div>

          <div className="border-t border-border my-1" />

          {/* Actions */}
          <button
            onClick={undo}
            disabled={annotations.length === 0}
            className="p-2 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-30 transition-colors"
            title="Annuler"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
          {onOpenFullPreview && (
            <button
              onClick={onOpenFullPreview}
              className="p-2 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
              title="Agrandir"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Canvas */}
        <div
          ref={containerRef}
          className="flex-1 rounded-md border border-border overflow-hidden bg-muted/30 relative"
        >
          {canvasSize.width > 0 && (
            <canvas
              ref={canvasRef}
              width={canvasSize.width}
              height={canvasSize.height}
              className="w-full h-auto cursor-crosshair"
              style={{
                cursor: getCursorStyle(activeTool),
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            />
          )}

          {/* Text input overlay */}
          {textInput && canvasSize.width > 0 && (
            <div
              className="absolute z-10"
              style={{
                left: `${(textInput.x / canvasSize.width) * 100}%`,
                top: `${(textInput.y / canvasSize.height) * 100}%`,
              }}
            >
              <input
                autoFocus
                className="bg-black/70 text-white text-sm px-2 py-1 rounded border border-white/30 outline-none min-w-[120px]"
                placeholder="Saisir le texte..."
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTextSubmit();
                  if (e.key === 'Escape') setTextInput(null);
                }}
                onBlur={handleTextSubmit}
              />
            </div>
          )}

          {!imageLoaded && (
            <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
              Chargement...
            </div>
          )}
        </div>
      </div>

      {annotations.length > 0 && (
        <p className="text-[10px] text-muted-foreground">
          {annotations.length} annotation{annotations.length > 1 ? 's' : ''} · Les annotations seront envoyées séparément à l'IA
        </p>
      )}
    </div>
  );
}
